import { useEffect, useRef, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const HISTORY_KEY = "hmcg.colorHistory.v1";
const MAX_HISTORY = 18;

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

function normalizeHex(v) {
  let h = (v || "").trim();
  if (!h.startsWith("#")) h = `#${h}`;
  if (/^#([0-9a-fA-F]{3})$/.test(h)) {
    h = "#" + h.slice(1).split("").map((c) => c + c).join("");
  }
  return /^#([0-9a-fA-F]{6})$/.test(h) ? h.toLowerCase() : null;
}

/**
 * Sleek color picker in a popover with live preview, hex entry, and a recent
 * colors history persisted to localStorage.
 */
export const ColorPickerPopover = ({ color, onChange, onCommit, testId, swatchTestId }) => {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(color);
  const [hexText, setHexText] = useState(color);
  const [history, setHistory] = useState(loadHistory);
  const debounceRef = useRef(null);

  useEffect(() => {
    setLocal(color);
    setHexText(color);
  }, [color]);

  const emit = (v) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onChange?.(v), 90);
  };

  const applyLive = (v) => {
    setLocal(v);
    setHexText(v);
    emit(v);
  };

  const addToHistory = (v) => {
    const norm = normalizeHex(v);
    if (!norm) return;
    setHistory((prev) => {
      const next = [norm, ...prev.filter((c) => c.toLowerCase() !== norm)].slice(0, MAX_HISTORY);
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota */
      }
      return next;
    });
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const norm = normalizeHex(local) || color;
      onChange?.(norm);
      onCommit?.(norm);
      addToHistory(norm);
    }
  };

  const onHexInput = (v) => {
    setHexText(v);
    const norm = normalizeHex(v);
    if (norm) {
      setLocal(norm);
      emit(norm);
    }
  };

  const pickRecent = (v) => {
    setLocal(v);
    setHexText(v);
    onChange?.(v);
    addToHistory(v);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={swatchTestId}
          className="relative w-9 h-9 rounded-md border border-zinc-700 cursor-pointer flex-shrink-0 overflow-hidden transition-all duration-200 hover:scale-105 hover:border-zinc-500 hover:shadow-md hover:shadow-black/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
          style={{ backgroundColor: local }}
          aria-label="Pick color"
          title="Pick color"
        />
      </PopoverTrigger>
      <PopoverContent
        data-testid={testId}
        align="start"
        sideOffset={8}
        className="w-60 border-zinc-700/80 bg-[#141519] p-3 shadow-2xl shadow-black/60"
      >
        <div className="space-y-3">
          <div className="hmcg-color-picker">
            <HexColorPicker color={local} onChange={applyLive} />
          </div>

          <div className="flex items-center gap-2">
            <span
              className="w-8 h-8 rounded-md border border-zinc-700 flex-shrink-0"
              style={{ backgroundColor: local }}
            />
            <div className="flex-1 flex items-center rounded-md border border-zinc-700 bg-[#0d0e12] px-2 focus-within:border-rose-500/70 transition">
              <span className="mono text-zinc-500 text-sm">#</span>
              <input
                data-testid={`${testId}-hex-input`}
                value={hexText.replace(/^#/, "")}
                onChange={(e) => onHexInput(e.target.value)}
                spellCheck={false}
                maxLength={7}
                className="w-full bg-transparent mono text-sm text-zinc-100 uppercase outline-none px-1 py-1.5"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Recent colours
            </div>
            {history.length === 0 ? (
              <div className="text-[11px] text-zinc-600 italic">
                Colours you pick will show up here.
              </div>
            ) : (
              <div className="grid grid-cols-9 gap-1.5" data-testid={`${testId}-history`}>
                {history.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => pickRecent(c)}
                    title={c}
                    className="group relative aspect-square rounded-[5px] border border-zinc-700/70 transition-transform hover:scale-110 hover:border-white/70"
                    style={{ backgroundColor: c }}
                  >
                    {c.toLowerCase() === (local || "").toLowerCase() && (
                      <Check className="absolute inset-0 m-auto w-3 h-3 text-white drop-shadow" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
